'use client';

import { ReactNode, useCallback } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { createAuth, numberClaim, SESSION_ERROR_MESSAGE, stringClaim } from '@upms/auth';
import { optOut as optOutAnalytics } from '@upms/analytics';
import { setTrackingConsent } from '@upms/observability';
import { authConfig } from '@/config/auth';

// Every account user is an insured whose ID token carries custom:account_id,
// and every API this app calls scopes by it.
const auth = createAuth({
  config: authConfig,
  extractSession: ({ idToken, claims }) => {
    const accountId = stringClaim(claims, 'custom:account_id');

    if (!accountId) {
      // must throw an error to prevent downstream requests
      throw new Error(SESSION_ERROR_MESSAGE);
    }

    return { accountId, idToken, authTime: numberClaim(claims, 'auth_time') };
  },
  extractHeaders: ({ accountId, idToken }) => ({
    authorization: `Bearer ${idToken}`,
    'x-pie-account-id': accountId,
  }),
});

export const useAuthContext = auth.useAuthContext;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();

  const onSignOut = useCallback(() => {
    setTrackingConsent(false);
    optOutAnalytics();
    queryClient.clear();
    window.location.assign('/sign-in');
  }, [queryClient]);

  return (
    <Authenticator.Provider>
      <auth.AuthProvider onSignOut={onSignOut}>{children}</auth.AuthProvider>
    </Authenticator.Provider>
  );
};

export const getSignInWithRedirect = () => {
  const redirect = `${window.location.pathname}${window.location.search}`;

  if (redirect !== '/') {
    return `/sign-in?redirect=${encodeURIComponent(redirect)}`;
  }

  return `/sign-in`;
};
